import { CaseAssistSettingsPanel } from '@/admin/CaseAssistSettingsPanel';
import { useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Tabs, Typography } from 'antd';
import { SettingOutlined } from '@ant-design/icons';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/components/pageTitle';
import { EntitlementMatrix } from '@/components/EntitlementMatrix';
import { UsersPanel } from '@/admin/UsersPanel';
import { RolesPanel } from '@/admin/RolesPanel';
import { PermissionMatrixPanel } from '@/admin/PermissionMatrixPanel';
import { LicensePanel } from '@/admin/LicensePanel';
import { ProjectsPanel } from '@/admin/ProjectsPanel';
import { DelegationsPanel } from '@/admin/DelegationsPanel';
import { PoliciesPanel } from '@/admin/PoliciesPanel';
import { FederationPanel } from '@/admin/FederationPanel';
import { SpokesPanel } from '@/admin/SpokesPanel';
import { ThemePanel } from '@/admin/ThemePanel';
import { ProfilePanel } from '@/admin/ProfilePanel';
import { QuotasPanel } from '@/admin/QuotasPanel';
import { MeteringPanel } from '@/admin/MeteringPanel';
import { AccessFencingPanel } from '@/admin/AccessFencingPanel';

const { Title, Paragraph } = Typography;

/** B1 — tenant admin console (F-TA-03/04/05, F-TA-09, F-AD-04). Hub-admin surface, API-enforced. */
export function AdminPage() {
  usePageTitle('Tenant admin');
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') ?? 'users';

  const items = useMemo(
    () => [
      { key: 'users', label: 'Users', children: <UsersPanel /> },
      // Team & Access (OPSFL-FSD-IAM-UC2 §12 slice 7): the console hides what the caller cannot
      // do; the SERVER re-checks every one of these — an unhidden affordance still 403s.
      { key: 'roles', label: 'Custom roles', children: <RolesPanel /> },
      // OPSFL-IAMUI-1 (FTM Team&Access model): the literal-mark key×role authority table. Read-only
      // — the matrix is compile-time authority (same file the server boot-loads), never console data.
      { key: 'matrix', label: 'Permission matrix', children: <PermissionMatrixPanel /> },
      { key: 'projects', label: 'Projects', children: <ProjectsPanel /> },
      { key: 'delegations', label: 'Delegations', children: <DelegationsPanel /> },
      // OPSFL-FLOWOPS-1 C3: the instance-ACL ledger (UC1 §4.7) — grants + confinements, read
      // and revoke; the "Assign to spoke" gesture lives on Flows and lands here.
      { key: 'fencing', label: 'Access fencing', children: <AccessFencingPanel /> },
      { key: 'policies', label: 'Policies', children: <PoliciesPanel /> },
      // OPSFL-UC2T-1 (FSD §9): enterprise IdP brokering — the G17 pins (FORCE sync, hub_user JIT
      // floor) render as facts, and the claim→role editor refuses platform_admin on both sides.
      { key: 'federation', label: 'Federation', children: <FederationPanel /> },
      { key: 'spokes', label: 'Spokes', children: <SpokesPanel /> },
      // OPSFL-THIAM-1: the /v1/theme* white-label console surface (end-customer plane branding;
      // the ledger flow draft → publish → revert, honestly scoped to what the API accepts).
      { key: 'branding', label: 'Branding / Theme', children: <ThemePanel /> },
      { key: 'profile', label: 'Regime & profile', children: <ProfilePanel /> },
      { key: 'quotas', label: 'Quotas & budgets', children: <QuotasPanel /> },
      // OL-31: the tenant-held kill switch and per-hub budget for suggestions on a case (ADR-006).
      { key: 'assist', label: 'Case assistance', children: <CaseAssistSettingsPanel /> },
      // OPSFL-IAMUI-1: the OL-COMM-01 commercial spine's first UI — read-only license/commercial
      // facts (contract, rate card, rated usage, license state, billing-wiring evidence).
      { key: 'license', label: 'My license', children: <LicensePanel /> },
      {
        key: 'entitlements',
        label: 'Entitlements',
        children: (
          <div style={{ maxWidth: 780 }}>
            <Title level={5} style={{ margin: 0 }}>
              Entitlement matrix
            </Title>
            <Paragraph type="secondary" style={{ margin: '4px 0 16px', fontSize: 13 }}>
              The identity-rail entitlements this hub holds (F-AD-04). A provider that requires an
              entitlement the hub lacks cannot be bound — this is the gate, read here.
            </Paragraph>
            <EntitlementMatrix />
          </div>
        ),
      },
      { key: 'metering', label: 'Metering', children: <MeteringPanel /> },
    ],
    [],
  );

  return (
    <div className="ol-page">
      <PageHeader
        title="Tenant admin"
        icon={<SettingOutlined />}
        subtitle={
          <>
            Administer this hub: people and their authority (users, roles, the permission matrix),
            access fencing (which spoke, user, role or project may see and act on which flows and
            cases), enterprise IdP federation, spokes and provisioning, white-label branding of the
            end-customer plane, the regime &amp; compliance profile, capability quotas and budgets,
            the hub&apos;s license and commercial facts, identity-rail entitlements, and metering.
            Changes here are enforced server-side by the permission matrix — the console just makes
            them legible.
          </>
        }
      />

      {/* Panes stay MOUNTED when inactive (no destroy-on-hide): tab state rides the URL, so a tab
          switch or browser back/forward would otherwise destroy the pane and silently discard
          unsaved Profile/Quotas edits. */}
      <Tabs
        activeKey={tab}
        onChange={(k) => {
          searchParams.set('tab', k);
          setSearchParams(searchParams, { replace: true });
        }}
        items={items}
      />
    </div>
  );
}
